import { NumberObjectHelper } from '@attain-apps/object-utils'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type * as core from '../../../core'
import * as common from '../../common'
import type { RowBasedReport } from '../row-based-report'

import { Allocation } from './allocation'

/**
 * Represents the total costs allocated to a {@link common.FaFunction.Category}
 * _before_ those costs are allocated to other categories
 */
export type Pool<TType extends Pool.Type = Pool.Type> =
  // NOTE: distributing over TType allows this to be a discriminated union
  TType extends any
    ? Pool.WithSubpools<TType> | Pool.WithoutSubpools<TType>
    : never
export namespace Pool {
  /**
   * Just a {@link common.FaFunction.Category.Id}
   */
  export type Id = common.FaFunction.Category.Id
  export namespace Id {
    export const isId =
      common.FaFunction.Category.Id.isId.usingAlias('rate.Pool.Id')
    export const assertIsId: TypeAsserter<Id> = TypeAsserter.Factory.from(isId)
  }

  /**
   * Determines if a {@link Pool} is {@link WithSubpools}
   *
   * If it is more convenient for type purposes, the negation,
   * {@link hasNoSubpools}, can also be used
   */
  // NOTE: the overloads handle a `Readonly.Deep` version of `Pool`
  export function hasSubpools<TType extends Type>(
    pool: Pool<TType>,
  ): pool is Extract<Pool<TType>, WithSubpools<TType>>
  export function hasSubpools<TType extends Type>(
    pool: Readonly.Deep<Pool<TType>>,
  ): pool is Extract<
    Readonly.Deep<Pool<TType>>,
    Readonly.Deep<WithSubpools<TType>>
  >
  export function hasSubpools(
    pool: Readonly.Deep<Pool>,
  ): pool is Extract<Readonly.Deep<Pool>, Readonly.Deep<WithSubpools>> {
    return !!(pool as FlattenUnion<typeof pool>).subpools
  }

  /**
   * Determines if a {@link Pool} is {@link WithoutSubpools}
   *
   * If it is more convenient for type purposes, the negation,
   * {@link hasSubpools}, can also be used
   */
  // NOTE: the overloads handle a `Readonly.Deep` version of `Pool`
  export function hasNoSubpools<TType extends Type>(
    pool: Pool<TType>,
  ): pool is Extract<Pool<TType>, WithoutSubpools<TType>>
  export function hasNoSubpools<TType extends Type>(
    pool: Readonly.Deep<Pool<TType>>,
  ): pool is Extract<
    Readonly.Deep<Pool<TType>>,
    Readonly.Deep<WithoutSubpools<TType>>
  >
  export function hasNoSubpools(
    pool: Readonly.Deep<Pool>,
  ): pool is Extract<Readonly.Deep<Pool>, Readonly.Deep<WithoutSubpools>> {
    return !hasSubpools(pool)
  }

  /**
   * Returns the total allocable amount from the given {@link Pool} of the
   * given {@link Type}
   *
   * @see {@link Values.getAllocableAmount}
   */
  // NOTE: the overloads handle a `Readonly.Deep` version of `WithSubpools`
  export function getSubpool<TType extends Type>(
    pool: WithSubpools<TType>,
    subpoolId: Subpool.Id,
  ): Subpool<TType> | undefined
  export function getSubpool<TType extends Type>(
    pool: Readonly.Deep<WithSubpools<TType>>,
    subpoolId: Subpool.Id,
  ): Readonly.Deep<Subpool<TType>> | undefined
  export function getSubpool<TType extends Type>(
    pool: Readonly.Deep<WithSubpools<TType>>,
    subpoolId: Subpool.Id,
  ): Readonly.Deep<Subpool<TType>> | undefined {
    return pool.subpools.find(subpool => subpool.id === subpoolId)
  }

  /**
   * Returns the total {@link Values} from the given {@link Pool}
   */

  export function getTotalValues<TType extends Type>(
    pool: Pool<TType>,
  ): Values<TType> {
    return hasSubpools<TType>(pool)
      ? WithSubpools.getTotalValues<TType>(pool)
      : WithoutSubpools.getTotalValues<TType>(
          // NOTE: since we use the `TType` type parameter, TypeScript doesn't
          // understand that this pool does not have subpools
          pool as WithoutSubpools<TType>,
        )
  }

  /**
   * Returns the total allocable amount from the given {@link Pool} of the
   * given {@link Type}
   *
   * @see {@link Values.getAllocableAmount}
   */
  export function getAllocableAmount(pool: Readonly.Deep<Pool>): number {
    return hasSubpools(pool)
      ? WithSubpools.getAllocableAmount(pool)
      : WithoutSubpools.getAllocableAmount(pool)
  }

  /**
   * Retrieves all {@link Pool.Entry} in a {@link Pool}
   *
   * This is mainly useful to handle both {@link Pool.WithSubpools} and
   * {@link Pool.WithoutSubpools}
   *
   * @see {@link getEntryIdSet}
   */
  export function getEntries<TType extends Type>(
    pool: Pool<TType>,
  ): Entry<TType>[]
  export function getEntries<TType extends Type>(
    pool: Readonly.Deep<Pool<TType>>,
  ): Readonly.Deep<Entry<TType>>[]
  export function getEntries(
    pool: Readonly.Deep<Pool>,
  ): Readonly.Deep<Entry>[] {
    return hasSubpools(pool)
      ? pool.subpools.flatMap<Readonly.Deep<Entry>>(subpool => subpool.entries)
      : [...pool.entries]
  }

  /**
   * Retrieves a {@link Set} containing the IDs of all {@link Pool.Entry} in a
   * {@link Pool}
   *
   * @see {@link getEntries}
   */
  export function getEntryIdSet(pool: Pool): Set<core.dms.data.Record.Id> {
    return new Set(getEntries(pool).map(entry => entry.id))
  }

  /**
   * Removes any {@link Entry} in the given {@link Pool} where
   * {@link Entry.isEmpty}
   *
   * **NOTE:** this is done in-place
   *
   * @see {@link Entry.Group.removeEmptyEntries}
   */
  export function removeEmptyEntries(pool: Pool): void {
    if (hasSubpools(pool)) {
      WithSubpools.removeEmptyEntries(pool)
    } else {
      WithoutSubpools.removeEmptyEntries(pool)
    }
  }

  /**
   * Properties that are common to both {@link WithSubpools} and
   * {@link WithoutSubpools}
   */
  interface Base<TType extends Type> extends core.common.Identified<Id> {
    type: TType
  }
  /**
   * {@link Pool} that has {@link Subpool subpools}
   *
   * @see {@link WithoutSubpools}
   */
  export interface WithSubpools<TType extends Type = Type> extends Base<TType> {
    /**
     * @see {@link Subpool}
     */
    // NOTE: intentionally not readonly to allow for iterative creation
    subpools: Subpool<TType>[]
  }
  export namespace WithSubpools {
    /**
     * Returns the total {@link Values} from the given
     * {@link Pool.WithSubpools}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function getTotalValues<TType extends Type>(
      pool: WithSubpools<TType>,
    ): Values<TType> {
      return Values.Helper.of(pool.type).combine(
        ...pool.subpools.flatMap(subpool =>
          subpool.entries.map(entry => entry.values),
        ),
      )
    }

    /**
     * Returns the total allocable amount from the given {@link Pool.WithSubpools} of the
     * given {@link Type}
     *
     * @see {@link Values.getAllocableAmount}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function getAllocableAmount(
      pool: Readonly.Deep<WithSubpools>,
    ): number {
      return pool.subpools.reduce(
        (total, subpool) =>
          total + Subpool.getAllocableAmount(pool.type, subpool),
        0,
      )
    }

    /**
     * Removes any {@link Entry} in the given {@link Pool.WithSubpools} where
     * {@link Entry.isEmpty}
     *
     * **NOTE:** this is done in-place
     *
     * @see {@link Entry.Group.removeEmptyEntries}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function removeEmptyEntries(pool: WithSubpools): void {
      for (const subpool of pool.subpools) {
        Subpool.removeEmptyEntries(pool.type, subpool)
      }
    }
  }
  /**
   * {@link Pool} that does NOT have {@link Subpool subpools}
   *
   * @see {@link WithSubpools}
   */
  export type WithoutSubpools<TType extends Type = Type> = Base<TType> &
    Entry.Group<TType>
  export namespace WithoutSubpools {
    /**
     * Returns the total {@link Values} from the given
     * {@link Pool.WithoutSubpools}
     *
     * @see {@link Entry.Group.getTotalValues}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function getTotalValues<TType extends Type>(
      pool: WithoutSubpools<TType>,
    ): Values<TType> {
      return Entry.Group.getTotalValues(pool.type, pool)
    }

    /**
     * Returns the total allocable amount from the given {@link Pool.WithoutSubpools} of the
     * given {@link Type}
     *
     * @see {@link Values.getAllocableAmount}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function getAllocableAmount(
      pool: Readonly.Deep<WithoutSubpools>,
    ): number {
      return Entry.Group.getAllocableAmount(pool.type, pool)
    }

    /**
     * Removes any {@link Entry} in the given {@link Pool.WithoutSubpools} where
     * {@link Entry.isEmpty}
     *
     * **NOTE:** this is done in-place
     *
     * @see {@link Entry.Group.removeEmptyEntries}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function removeEmptyEntries(pool: WithoutSubpools): void {
      Entry.Group.removeEmptyEntries(pool.type, pool)
    }
  }

  /**
   * Represents an {@link Entry.Group} corresponding to a
   * {@link common.FaFunction.Subcategory} in a given {@link Pool}
   */
  export type Subpool<TType extends Type = Type> =
    core.common.Identified<Subpool.Id> & Entry.Group<TType>
  export namespace Subpool {
    /**
     * Typically a {@link common.FaFunction.Subcategory.Id}
     *
     * However, it can also be {@link Id.FOR_NO_SUBCATEGORY}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Id =
      | common.FaFunction.Subcategory.Id
      | typeof Id.FOR_NO_SUBCATEGORY
    export namespace Id {
      /**
       * Used in cases where the {@link Pool} is not associated with any
       * particular {@link common.FaFunction.Subcategory}
       */
      export const FOR_NO_SUBCATEGORY = Symbol.for(
        '@attain-apps/rate.Subpool.Id.FOR_NO_SUBCATEGORY',
      )

      export const isId = TypeChecker.Factory.union(
        common.FaFunction.Subcategory.Id.isId,
        TypeChecker.Factory.exact(FOR_NO_SUBCATEGORY),
      ).usingAlias('rate.Pool.Subpool.Id')
      export const assertIsId: TypeAsserter<Id> =
        TypeAsserter.Factory.from(isId)

      /**
       * Used to store arbitrary values by {@link Subpool.Id}
       */
      export type BasedLookup<T> = Partial<Record<Id, T>>
      export namespace BasedLookup {
        /**
         * By default, JavaScript does not expose {@link Symbol} keys, which we
         * need in this case. So we use this to ensure we get all expected
         * values from the {@link Subpool.Id.BasedLookup}
         */
        export function entries<T>(
          lookup: Readonly<BasedLookup<T>>,
        ): readonly (readonly [Id, T])[] {
          return [...Object.keys(lookup), FOR_NO_SUBCATEGORY]
            .filter(isId)
            .map<readonly [Id, T] | undefined>(id => {
              const value = lookup[id]
              return TypeChecker.isNullish(value) ? undefined : [id, value]
            })
            .filter(TypeChecker.isNotNullish.raw)
        }

        /**
         * Determines if {@link Subpool.Id.BasedLookup} only has an entry for
         * {@link Subpool.Id.FOR_NO_SUBCATEGORY}
         *
         * This can sometimes be useful when trying to determine if the lookup
         * should be used to produce a {@link Pool.WithoutSubpools} vs a
         * {@link Pool.WithSubpools}
         */
        export function onlyHasEntryForNoSubcategory<
          TLookup extends BasedLookup<any>,
        >(
          lookup: TLookup,
        ): lookup is TLookup &
          Required.Semi<TLookup, typeof FOR_NO_SUBCATEGORY> {
          return (
            // Has FOR_NO_SUBCATEGORY
            lookup[FOR_NO_SUBCATEGORY] !== undefined &&
            // Has no other Subpool IDs
            !Object.keys(lookup).some(key => isId(key))
          )
        }
      }
    }

    /**
     * Returns the total {@link Values} from the given {@link Subpool} of the
     * given {@link Type}
     *
     * @see {@link Entry.Group.getTotalValues}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function getTotalValues<TType extends Type>(
      type: TType,
      subpool: Subpool<TType>,
    ): Values<TType> {
      return Entry.Group.getTotalValues(type, subpool)
    }

    /**
     * Returns the total allocable amount from the given {@link Subpool} of the
     * given {@link Type}
     *
     * @see {@link Values.getAllocableAmount}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function getAllocableAmount(
      type: Type,
      subpool: Readonly.Deep<Subpool>,
    ): number {
      return Entry.Group.getAllocableAmount(type, subpool)
    }

    /**
     * Removes any {@link Entry} in the given {@link Subpool} where
     * {@link Entry.isEmpty}
     *
     * **NOTE:** this is done in-place
     *
     * @see {@link Entry.Group.removeEmptyEntries}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function removeEmptyEntries(type: Type, subpool: Subpool): void {
      Entry.Group.removeEmptyEntries(type, subpool)
    }

    /**
     * Sorts {@link Subpool subpools} by {@link Subpool.Id id}, with
     * {@link Id.FOR_NO_SUBCATEGORY} at the end
     *
     * This is mostly used to ensure that results are returned in a consistent
     * order
     */
    export function sort<TSubpool extends Subpool>(
      subpools: readonly TSubpool[],
    ): TSubpool[] {
      return [...subpools].sort((a, b) =>
        a.id === Id.FOR_NO_SUBCATEGORY
          ? b.id === Id.FOR_NO_SUBCATEGORY
            ? 0
            : 1 // Puts FOR_NO_SUBCATEGORY at the end
          : b.id === Id.FOR_NO_SUBCATEGORY
            ? -1 // Puts FOR_NO_SUBCATEGORY at the end
            : a.id.localeCompare(b.id),
      )
    }
  }

  /**
   * Each {@link Pool} can be broken down by some unit to show support for the
   * total values. For example, if I tell you that pool `XYZ` has $100, to show
   * that I am not just making that number up, I can show you the entries that
   * support that total:
   *
   * | Entry | Amount |
   * | :-- | --: |
   * | Department 1 | $50 |
   * | Department 2 | $30 |
   * | Department 3 | $20 |
   * | **Total:** | $100  |
   */
  export type Entry<TType extends Type = Type> =
    core.common.Identified<core.dms.data.Record.Id> &
      RowBasedReport.Entry<Values<TType>>
  export namespace Entry {
    /**
     * Returns the total allocable amount from the given {@link Entry} of the
     * given {@link Type}
     *
     * @see {@link Values.getAllocableAmount}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function getAllocableAmount(
      type: Type,
      entry: Readonly.Deep<Entry>,
    ): number {
      return Values.Helper.of(type).getAllocableAmount(entry.values)
    }

    /**
     * Indicates the {@link Values} of the {@link Entry} are effectively empty.
     * I.e., the {@link Entry} can generally be ignored
     *
     * @see {@link Values.isEmpty}
     */
    export function isEmpty(type: Type, entry: Readonly.Deep<Entry>): boolean {
      return Values.Helper.of(type).isEmpty(entry.values)
    }

    /**
     * Represents a set of {@link Entry entries}. Usually, this is for a
     * {@link Pool} or {@link Subpool}
     */
    export interface Group<TType extends Type = Type> {
      /**
       * If the group {@link entity} is defined, then each {@link Entry} of
       * {@link entries} is a record of this {@link dms.models.Entity}
       */
      entity?: core.dms.models.Entity.Id
      /**
       * @see {@link Entry}
       */
      // NOTE: intentionally not readonly to allow for iterative creation
      entries: Entry<TType>[]
      /**
       * Additional {@link Values} for this {@link Group} that are not captured in the {@link entries}
       */
      additionalValues?: Values<TType>
    }
    export namespace Group {
      /**
       * Returns the total {@link Values} from the given {@link Group} of the
       * given {@link Type}, using {@link Values.Helper.combine}
       */
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function getTotalValues<TType extends Type>(
        type: TType,
        group: Group<TType>,
      ): Values<TType> {
        return Values.Helper.of(type).combine(
          ...group.entries.map(entry => entry.values),
        )
      }

      /**
       * Returns the total allocable amount from the given {@link Group} of the
       * given {@link Type}
       *
       * @see {@link Values.getAllocableAmount}
       */
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function getAllocableAmount(
        type: Type,
        group: Readonly.Deep<Group>,
      ): number {
        return group.entries.reduce(
          (total, entry) => total + Entry.getAllocableAmount(type, entry),
          // If there are additional values, we must include that in the sum
          group.additionalValues
            ? Values.Helper.of(type).getAllocableAmount(group.additionalValues)
            : 0,
        )
      }

      /**
       * Removes any {@link Entry} in the given {@link Group} where
       * {@link Entry.isEmpty}
       *
       * **NOTE:** this is done in-place
       *
       * @see {@link Entry.isEmpty}
       */
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export function removeEmptyEntries(type: Type, group: Group): void {
        group.entries = group.entries.filter(
          entry => !Entry.isEmpty(type, entry),
        )
      }
    }
  }

  /**
   * Each {@link Pool.Type} has their own set of values that are relevant for
   * that type
   */
  export type Values<TType extends Type = Type> = {
    [Type.Depreciation]: OfDepreciation.Values
    [Type.Interest]: OfInterest.Values
    [Type.Gl]: OfGl.Values
  }[TType]
  export namespace Values {
    /**
     * Several utilities for working with a particular type of
     * {@link Values}
     */
    export class Helper<TType extends Type = Type> extends NumberObjectHelper<
      Values<TType>
    > {
      /**
       * Used to lookup {@link Helper} by {@link Type}
       *
       * NOTE: cannot be instantiated immediately because the instances won't
       * exist yet
       */
      private static _instances: { [K in Type]: Helper<K> } | undefined

      /**
       * Returns the total allocable amount from the given {@link Values} of the
       * given {@link Type}
       */

      public static of<TType extends Type>(type: TType): Helper<TType> {
        return (this._instances ??= {
          [Type.Depreciation]: OfDepreciation.Values.HELPER,
          [Type.Interest]: OfInterest.Values.HELPER,
          [Type.Gl]: OfGl.Values.HELPER,
        })[type]
      }

      constructor(
        private readonly _params: Readonly<
          Pick<Helper<TType>, 'getAllocableAmount' | 'zero'> &
            Omit<
              NumberObjectHelper.constructor.Params<Values<TType>>,
              'irrelevancePrecision'
            >
        >,
      ) {
        super({
          ..._params,
          irrelevancePrecision: 1,
        })
        this.getAllocableAmount = _params.getAllocableAmount
      }

      /**
       * Returns the total allocable amount from the given {@link Values}
       */
      public getAllocableAmount(values: Values<TType>): number {
        return this._params.getAllocableAmount(values)
      }

      /**
       * @see {@link Helper}
       */
      public override zero(): Values<TType> {
        return this._params.zero()
      }
    }
  }

  /**
   * Each {@link Pool} represents a set of costs. This represents the different
   * types of costs that are used in Rate
   */
  export enum Type {
    /** @see {@link OfDepreciation} */
    Depreciation = 'Depreciation',
    /** @see {@link OfInterest} */
    Interest = 'Interest',
    /** @see {@link OfGl} */
    Gl = 'GL',
  }
  // Needs to be before namespace is defined
  const TYPES: ReadonlySet<Type> = new Set(Object.values(Type) as Type[])
  export namespace Type {
    export const ALL: readonly Type[] = Array.from(TYPES)
    export const isType = TypeChecker.Factory.oneOf(TYPES)
    export const assertIsType: TypeAsserter<Type> =
      TypeAsserter.Factory.from(isType)
  }

  /**
   * A {@link Pool} that is based on depreciation amounts
   */
  export type OfDepreciation = Pool<Type.Depreciation>
  export namespace OfDepreciation {
    /**
     * Shorthand for {@link Pool.WithSubpools}<{@link Pool.Type.Depreciation}>
     *
     * Uses {@link OfDepreciation.Values}
     */

    export type WithSubpools = Pool.WithSubpools<Type.Depreciation>

    /**
     * Shorthand for {@link Pool.WithoutSubpools}<{@link Pool.Type.Depreciation}>
     *
     * Uses {@link OfDepreciation.Values}
     */

    export type WithoutSubpools = Pool.WithoutSubpools<Type.Depreciation>

    /**
     * Shorthand for {@link Pool.Subpool}<{@link Pool.Type.Depreciation}>
     *
     * Uses {@link OfDepreciation.Values}
     */

    export type Subpool = Pool.Subpool<Type.Depreciation>

    /**
     * Shorthand for {@link Pool.Entry}<{@link Pool.Type.Depreciation}>
     *
     * Uses {@link OfDepreciation.Values}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Entry = Pool.Entry<Type.Depreciation>
    export namespace Entry {
      /**
       * Shorthand for {@link Pool.Entry.Group}<{@link Pool.Type.Depreciation}>
       *
       * Uses {@link OfDepreciation.Values}
       */
      export type Group = Pool.Entry.Group<Type.Depreciation>
    }

    /**
     * Important numbers when explaining a {@link Pool.OfDepreciation}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export interface Values {
      acquisitionCost: number
      totalDepreciation: number
      federalAdjustment: number
      otherAdjustment: number
    }
    export namespace Values {
      const KEYS = [
        'acquisitionCost',
        'federalAdjustment',
        'otherAdjustment',
        'totalDepreciation',
      ] as const satisfies readonly (keyof Values)[]
      /**
       * NOTE: this line just ensures that {@link KEYS} has exactly the right keys
       */
      KEYS satisfies readonly Satisfies<keyof Values, (typeof KEYS)[number]>[]
      /**
       * @see {@link Pool.Values.Helper}
       */
      export const HELPER = new Pool.Values.Helper<Type.Depreciation>({
        getAllocableAmount: values => getNetDepreciation(values),
        keys: KEYS,
        zero: () => ({
          acquisitionCost: 0,
          federalAdjustment: 0,
          otherAdjustment: 0,
          totalDepreciation: 0,
        }),
      })

      /**
       * Returns net depreciation (total - federal adjustment - other adjustment).
       * This is the amount that actually gets allocated in Rate.
       */
      export function getNetDepreciation(values: Readonly<Values>): number {
        return (
          values.totalDepreciation -
          values.federalAdjustment -
          values.otherAdjustment
        )
      }
    }
  }

  /**
   * A {@link Pool} that is based on interest expense amounts
   */
  export type OfInterest = Pool<Type.Interest>
  export namespace OfInterest {
    /**
     * Shorthand for {@link Pool.WithSubpools}<{@link Pool.Type.Interest}>
     *
     * Uses {@link OfInterest.Values}
     */

    export type WithSubpools = Pool.WithSubpools<Type.Interest>

    /**
     * Shorthand for {@link Pool.WithoutSubpools}<{@link Pool.Type.Interest}>
     *
     * Uses {@link OfInterest.Values}
     */

    export type WithoutSubpools = Pool.WithoutSubpools<Type.Interest>

    /**
     * Shorthand for {@link Pool.Subpool}<{@link Pool.Type.Interest}>
     *
     * Uses {@link OfInterest.Values}
     */

    export type Subpool = Pool.Subpool<Type.Interest>

    /**
     * Shorthand for {@link Pool.Entry}<{@link Pool.Type.Interest}>
     *
     * Uses {@link OfInterest.Values}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Entry = Pool.Entry<Type.Interest>
    export namespace Entry {
      /**
       * Shorthand for {@link Pool.Entry.Group}<{@link Pool.Type.Interest}>
       *
       * Uses {@link OfInterest.Values}
       */
      export type Group = Pool.Entry.Group<Type.Interest>
    }

    /**
     * Important numbers when explaining a {@link Pool.OfInterest}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export interface Values {
      totalInterest: number
      federalAdjustment: number
    }
    export namespace Values {
      const KEYS = [
        'totalInterest',
        'federalAdjustment',
      ] as const satisfies readonly (keyof Values)[]
      /**
       * NOTE: this line just ensures that {@link KEYS} has exactly the right keys
       */
      KEYS satisfies readonly Satisfies<keyof Values, (typeof KEYS)[number]>[]
      /**
       * @see {@link Pool.Values.Helper}
       */
      export const HELPER = new Pool.Values.Helper<Type.Interest>({
        getAllocableAmount: values => getNetInterest(values),
        keys: KEYS,
        zero: () => ({
          federalAdjustment: 0,
          totalInterest: 0,
        }),
      })

      /**
       * Returns net interest (total - federal adjustment). This is the amount
       * that actually gets allocated in Rate.
       */
      export function getNetInterest(values: Readonly<Values>): number {
        return values.totalInterest - values.federalAdjustment
      }
    }
  }

  /**
   * Shorthand for {@link Pool}<{@link Pool.Type.Gl}>
   *
   * Uses {@link OfGl.Values}
   */
  export type OfGl = Pool<Type.Gl>
  export namespace OfGl {
    /**
     * Shorthand for {@link Pool.WithSubpools}<{@link Pool.Type.Gl}>
     *
     * Uses {@link OfGl.Values}
     */

    export type WithSubpools = Pool.WithSubpools<Type.Gl>

    /**
     * Shorthand for {@link Pool.WithoutSubpools}<{@link Pool.Type.Gl}>
     *
     * Uses {@link OfGl.Values}
     */

    export type WithoutSubpools = Pool.WithoutSubpools<Type.Gl>

    /**
     * Shorthand for {@link Pool.Subpool}<{@link Pool.Type.Gl}>
     *
     * Uses {@link OfGl.Values}
     */

    export type Subpool = Pool.Subpool<Type.Gl>

    /**
     * Shorthand for {@link Pool.Entry}<{@link Pool.Type.Gl}>
     *
     * Uses {@link OfGl.Values}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Entry = Pool.Entry<Type.Gl>
    export namespace Entry {
      /**
       * Shorthand for {@link Pool.Entry.Group}<{@link Pool.Type.Gl}>
       *
       * Uses {@link OfGl.Values}
       */
      export type Group = Pool.Entry.Group<Type.Gl>
    }

    /**
     * Dollar amounts for each {@link common.MtdcCategory}
     *
     * {@link Values.getLabor}, {@link Values.getNonLabor}, and
     * {@link Values.getTotal} can be used to extract relevant dollar amounts.
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export type Values = Record<common.MtdcCategory, number>
    export namespace Values {
      /**
       * @see {@link Pool.Values.Helper}
       */
      export const HELPER = new Pool.Values.Helper<Type.Gl>({
        getAllocableAmount: values => getTotal(values),
        keys: common.MtdcCategory.ALL,
        zero: () => ({
          [common.MtdcCategory.Salary]: 0,
          [common.MtdcCategory.NonSalary]: 0,
          [common.MtdcCategory.Fringe]: 0,
        }),
      })

      /**
       * Sums all {@link common.MtdcCategory.ForLabor} dollar amounts in the given {@link Values}
       */
      export function getLabor(values: Readonly<Values>): number {
        return _getTotalForMtdcCategories(
          values,
          common.MtdcCategory.ForLabor.ALL,
        )
      }
      /**
       * Sums all {@link common.MtdcCategory.ForNonLabor} dollar amounts in the given {@link Values}
       */
      export function getNonLabor(values: Readonly<Values>): number {
        return _getTotalForMtdcCategories(
          values,
          common.MtdcCategory.ForNonLabor.ALL,
        )
      }
      /**
       * Sum of {@link getLabor} and {@link getNonLabor}
       */

      export function getTotal(values: Readonly<Values>): number {
        return getLabor(values) + getNonLabor(values)
      }

      /**
       * Sums dollar amounts in the given {@link Values} that match any of the
       * given {@link common.MtdcCategory}
       */
      function _getTotalForMtdcCategories(
        values: Readonly<Values>,
        mtdcCategories: readonly common.MtdcCategory[],
      ): number {
        return mtdcCategories.reduce(
          (total, mtdcCategory) => total + (values[mtdcCategory] ?? 0),
          0,
        )
      }
    }
  }

  /**
   * @see {@link Mappings.Summary}
   */
  export namespace Mappings {
    /**
     * Shows where the dollars in each {@link Pool} came from, split by
     * {@link Type}
     */
    export type Summary = Record<Type, readonly Allocation.Distribution.Entry[]>

    /**
     * Represents a distinct part/section of a {@link Mappings.Summary}
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export enum Type {
      /**
       * Simple mappings of amounts from an institution's financial statement
       * functions to the relevant IDC functions
       */
      Reclassification = 'reclassifications',
      /**
       * Removals/subtractions from {@link Reclassification}
       */
      Exclusion = 'exclusions',
      /**
       * Any other additions/subtractions to the various IDC functions
       */
      Adjustment = 'adjustments',
    }
    // Needs to be before namespace is defined
    const SOURCE_TYPES: ReadonlySet<Type> = new Set(
      Object.values(Type) as Type[],
    )
    export namespace Type {
      export const ALL: readonly Type[] = Array.from(SOURCE_TYPES)
      export const isType = TypeChecker.Factory.oneOf(SOURCE_TYPES)
      export const assertIsType: TypeAsserter<Type> =
        TypeAsserter.Factory.from(isType)
    }
  }

  /**
   * Contains the pool total including allocations from other indirect
   * categories (cross-allocations).
   *
   * This is useful for the cases where we don't need the final distribution of
   * the pool, but if that the case, {@link Allocation.Distribution.OfPool}
   * should be used instead.
   */
  export interface TotalWithCrossAllocations
    extends core.common.Identified<Pool.Id> {
    /**
     * This is the amount that the {@link Pool} has mapped to it before any
     * allocations have been made
     */
    naturalPoolAmount: number

    /**
     * This is the amount that the {@link Pool} has mapped to it before any
     * allocations have been made, but only for GL pools that are on campus.
     * This is used to calculate facility allocation costs.
     */
    naturalPoolAmountOnCampus: number

    /** @see {@link TotalWithCrossAllocations.CrossAllocationsByType} */
    crossAllocationsByType?: TotalWithCrossAllocations.CrossAllocationsByType
  }
  export namespace TotalWithCrossAllocations {
    /**
     * If there are allocations from other
     * {@link common.FaFunction.Category categories} to this
     * {@link common.FaFunction.Category}, these are those allocations grouped
     * by {@link common.FaFunction.Type.OfIndirect}
     */
    export type CrossAllocationsByType = Record<
      common.FaFunction.Type.OfIndirect,
      Allocation.ByFaCategory
    >
    /**
     * Returns the total dollars in {@link TotalWithCrossAllocations} that can
     * be allocated in {@link TotalWithCrossAllocations.crossAllocationsByType}
     *
     * The value returned by this function is useful for checking for allocation
     * issues. If there are no issues in the data and calculations, this value
     * should be equal to the {@link ValueType.Currency} value from
     * {@link Distribution.getTotalAllocation}. In other words, the total amount
     * that _can be_ allocated should be the same as the total amount that _is_
     * allocated. However, if there are issues in either the data or the
     * calculation logic, the two values may not be the same.
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    export function getAllocableAmount({
      naturalPoolAmount,
      crossAllocationsByType,
    }: Readonly.Deep<
      // These are the only properties we need, so using `Pick` makes this
      // function more flexible
      Pick<
        TotalWithCrossAllocations,
        'naturalPoolAmount' | 'crossAllocationsByType'
      >
    >): number {
      // Start with the natural pool amount
      let totalPoolAmount = naturalPoolAmount
      if (crossAllocationsByType) {
        // If there are cross allocations, combine them
        const combinedCrossAllocations = Allocation.Lookup.combine(
          ...Object.values(crossAllocationsByType),
        )
        // and add that in
        totalPoolAmount += Allocation.Lookup.getTotalAllocation(
          combinedCrossAllocations,
        )[Allocation.ValueType.Currency]
      }
      // Return the total
      return totalPoolAmount
    }

    /**
     * Returns the total {@link Allocation} for the given
     * {@link common.FaFunction.Type.OfIndirect type} by summing values in the
     * {@link crossAllocationsByType cross-allocations}
     */
    export function getTotalCrossAllocationForType({
      type,
      crossAllocationsByType,
    }: Readonly<{
      readonly type: Readonly<common.FaFunction.Type.OfIndirect>
      readonly crossAllocationsByType: Readonly<CrossAllocationsByType>
    }>): Allocation {
      return Allocation.Lookup.getTotalAllocation(crossAllocationsByType[type])
    }

    /**
     * Returns the total {@link Allocation} by summing the values for each
     * {@link common.FaFunction.Type.OfIndirect type} in the
     * {@link CrossAllocationsByType cross-allocations}
     */
    export function getTotalCrossAllocation(
      crossAllocationsByType: Readonly<CrossAllocationsByType>,
    ): Allocation {
      return common.FaFunction.Type.OfIndirect.ALL.reduce<Allocation>(
        (totalAllocation, type) =>
          Allocation.Helper.INSTANCE.combine(
            totalAllocation,
            getTotalCrossAllocationForType({ crossAllocationsByType, type }),
          ),
        Allocation.Helper.INSTANCE.zero(),
      )
    }

    /**
     * Indicates that the given {@link Pool.TotalWithCrossAllocations} has some
     * cross allocations
     *
     * __NOTE:__ this does *NOT* guarantee that any of the cross allocations are
     * non-zero or that there are cross allocations from every applicable
     * {@link common.FaFunction.Category}. It only guarantees that this pool has
     * something in terms of cross allocations
     */
    export function hasCrossAllocations(
      // Pick just makes the function signature more flexible for consumers
      {
        crossAllocationsByType,
      }: Readonly<Pick<TotalWithCrossAllocations, 'crossAllocationsByType'>>,
    ): boolean {
      if (!crossAllocationsByType) {
        // Clearly this has no cross allocations
        return false
      }
      // Must have at least one cross allocation for one of the potential types
      return common.FaFunction.Type.OfIndirect.ALL.some(
        faType =>
          Allocation.Lookup.entries(crossAllocationsByType[faType]).length >= 1,
      )
    }

    /**
     * Government has set this at 26%
     */
    const ADMIN_COST_LIMITATION = 0.26

    /**
     * Calculates the allowed administrative indirect cost rate within the
     * {@link ADMIN_COST_LIMITATION} by summing the values from the
     * {@link crossAllocationsByType} for the
     * {@link common.FaFunction.Type.Administration administration type}
     */
    export function getLimitedTotalAdministrativeIndirectCostRate(
      crossAllocationsByType: Readonly<CrossAllocationsByType>,
    ): number {
      const totalAdministrativeCostRate = getTotalCrossAllocationForType({
        crossAllocationsByType,
        type: common.FaFunction.Type.Administration,
      })['%']
      return Math.min(ADMIN_COST_LIMITATION, totalAdministrativeCostRate)
    }

    /**
     * Calculates the total allowed indirect cost rate within the
     * {@link ADMIN_COST_LIMITATION} by summing total
     * {@link common.FaFunction.Type.Facilities facilities} indirect cost rate
     * {@link totalFacilitiesIndirectCostRate} and the limited total
     * {@link common.FaFunction.Type.Administration administrative} indirect
     * cost rate
     *
     * @see {@link getLimitedTotalAdministrativeIndirectCostRate}
     */
    export function getLimitedTotalIndirectCostRate(
      crossAllocationsByType: Readonly<CrossAllocationsByType>,
    ): number {
      const totalFacilitiesIndirectCostRate = getTotalCrossAllocationForType({
        crossAllocationsByType,
        type: common.FaFunction.Type.Facilities,
      })['%']
      return (
        totalFacilitiesIndirectCostRate +
        getLimitedTotalAdministrativeIndirectCostRate(crossAllocationsByType)
      )
    }
  }
}
